import { ChevronLeft, ChevronRight, Loader2, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";

import { EventsErrorState } from "@/components/events/EventsErrorState";
import { ClearReviewQueueDialog } from "@/components/review/ClearReviewQueueDialog";
import { ReviewCard } from "@/components/review/ReviewCard";
import { ReviewCardSkeleton } from "@/components/review/ReviewCardSkeleton";
import { ReviewEmptyState } from "@/components/review/ReviewEmptyState";
import { Button } from "@/components/ui/button";
import { useClearReviewQueue } from "@/hooks/useEventActions";
import { useReviewQueue } from "@/hooks/useReviewQueue";

export function ReviewPage() {
  const query = useReviewQueue();
  const response = query.data;
  const items = response?.items ?? [];
  const isInitialLoading = query.isLoading && response === undefined;

  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const clearMutation = useClearReviewQueue();

  function onConfirmClear() {
    clearMutation.mutate(undefined, {
      onSuccess: () => setClearDialogOpen(false),
    });
  }

  // Positional cursor: the index drives which card we display. Keeping an
  // index (not an id) means that when approve/reject optimistically removes
  // items[cursor], the same index now points at the next event, which is
  // exactly the UX we want.
  const [cursor, setCursor] = useState(0);

  // Clamp the cursor whenever the list shrinks below it (e.g. the last card
  // was just approved): snap to the new last item.
  useEffect(() => {
    if (items.length > 0 && cursor >= items.length) {
      setCursor(items.length - 1);
    }
  }, [items.length, cursor]);

  const current = items[cursor];
  const canPrev = cursor > 0;
  const canNext = cursor < items.length - 1;

  const goPrev = () => {
    if (canPrev) setCursor((c) => c - 1);
  };
  const goNext = () => {
    if (canNext) setCursor((c) => c + 1);
  };

  // Global left/right arrow navigation, but only when the focused element
  // isn't an editable field (inputs/textarea/contenteditable) — otherwise we
  // would fight with cursor movement inside form fields.
  useEffect(() => {
    const isEditable = (el: EventTarget | null): boolean => {
      if (!(el instanceof HTMLElement)) return false;
      if (el.tagName === "INPUT" || el.tagName === "TEXTAREA") return true;
      if (el.tagName === "SELECT") return true;
      if (el.isContentEditable) return true;
      return false;
    };
    const onKey = (e: KeyboardEvent) => {
      if (isEditable(e.target)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === "ArrowLeft" && canPrev) {
        e.preventDefault();
        goPrev();
      } else if (e.key === "ArrowRight" && canNext) {
        e.preventDefault();
        goNext();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [canPrev, canNext]);

  return (
    <div className="container max-w-4xl space-y-6 py-8">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold tracking-tight">
              Coda revisione
            </h1>
            {query.isFetching && response !== undefined ? (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            ) : null}
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {response && response.total > 0
              ? `${response.total} ${
                  response.total === 1 ? "evento" : "eventi"
                } in attesa di revisione manuale.`
              : "Eventi incerti che richiedono revisione manuale prima di finire sul calendario."}
          </p>
        </div>
        {response && response.total > 0 ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="shrink-0 gap-1.5 text-muted-foreground hover:text-destructive"
            onClick={() => setClearDialogOpen(true)}
          >
            <Trash2 className="h-3.5 w-3.5" />
            Svuota coda
          </Button>
        ) : null}
      </div>

      {isInitialLoading ? (
        <ReviewCardSkeleton />
      ) : query.isError ? (
        <EventsErrorState
          message={
            query.error instanceof Error ? query.error.message : undefined
          }
          onRetry={() => void query.refetch()}
        />
      ) : items.length === 0 ? (
        <ReviewEmptyState />
      ) : current ? (
        <div className="space-y-3">
          <NavBar
            position={cursor + 1}
            total={items.length}
            canPrev={canPrev}
            canNext={canNext}
            onPrev={goPrev}
            onNext={goNext}
          />
          {/* Key by id so the ReviewCard unmounts when navigating; this
              guarantees a fresh react-hook-form instance per event and
              avoids leaking dirty state across cards. */}
          <ReviewCard key={current.id} event={current} />
        </div>
      ) : null}

      <ClearReviewQueueDialog
        total={response?.total ?? 0}
        open={clearDialogOpen}
        onOpenChange={setClearDialogOpen}
        onConfirm={onConfirmClear}
        pending={clearMutation.isPending}
      />
    </div>
  );
}

interface NavBarProps {
  position: number;
  total: number;
  canPrev: boolean;
  canNext: boolean;
  onPrev: () => void;
  onNext: () => void;
}

function NavBar({
  position,
  total,
  canPrev,
  canNext,
  onPrev,
  onNext,
}: NavBarProps) {
  return (
    <div className="flex items-center justify-between rounded-md border bg-card/50 px-3 py-2">
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={onPrev}
        disabled={!canPrev}
        aria-label="Evento precedente"
      >
        <ChevronLeft className="h-4 w-4" />
        Precedente
      </Button>
      <div className="text-sm text-muted-foreground tabular-nums">
        <span className="font-medium text-foreground">{position}</span>
        <span className="mx-1">di</span>
        <span>{total}</span>
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={onNext}
        disabled={!canNext}
        aria-label="Evento successivo"
      >
        Successivo
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  );
}
